export enum CommunicationChannel {
  EMAIL = "email",
  SMS = "sms",
  FAX = "fax",
}

export enum DeliveryMode {
  REAL_TIME = "realtime",
  SUMMARY = "summary",
}

export enum SummaryFrequency {
  DAILY = "daily",
  WEEKLY = "weekly",
}

export enum WeekDay {
  SUNDAY = "sunday",
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
}

export enum DeliveryHistoryChannel {
  EMAIL = "EMAIL",
  SMS = "SMS",
  FAX = "FAX",
}

export enum DeliveryHistoryStatus {
  SENT = "sent",
  FAILED = "failed",
  SUPPRESSED = "suppressed",
}

export const COMMUNICATION_CHANNEL_OPTIONS = [
  {
    key: CommunicationChannel.EMAIL,
    label: "Email",
    description: "Receive notifications at your registered email address.",
  },
  {
    key: CommunicationChannel.SMS,
    label: "SMS",
    description: "Receive SMS on your verified mobile number.",
  },
  {
    key: CommunicationChannel.FAX,
    label: "Fax",
    description: "Receive notifications via fax to your office fax number.",
  },
] as const;

export const DELIVERY_MODE_OPTIONS = [
  {
    key: DeliveryMode.REAL_TIME,
    label: "As they happen",
    description: "Receive notifications immediately as events occur.",
  },
  {
    key: DeliveryMode.SUMMARY,
    label: "Scheduled summary",
    description:
      "Batch notifications into a scheduled digest at your preferred time.",
  },
] as const;

export const SUMMARY_FREQUENCY_OPTIONS = [
  { key: SummaryFrequency.DAILY, label: "Daily" },
  { key: SummaryFrequency.WEEKLY, label: "Weekly" },
] as const;

export const WEEK_DAY_OPTIONS = [
  { key: WeekDay.MONDAY, label: "Monday" },
  { key: WeekDay.TUESDAY, label: "Tuesday" },
  { key: WeekDay.WEDNESDAY, label: "Wednesday" },
  { key: WeekDay.THURSDAY, label: "Thursday" },
  { key: WeekDay.FRIDAY, label: "Friday" },
  { key: WeekDay.SATURDAY, label: "Saturday" },
  { key: WeekDay.SUNDAY, label: "Sunday" },
] as const;

export const DEFAULT_CHANNELS: CommunicationChannel[] = [
  CommunicationChannel.EMAIL,
];

export const LIMS_NOTIFICATION_TYPES = [
  { id: "lims-order-received", key: "order_received", name: "Order Received" },
  {
    id: "lims-tissue-received",
    key: "tissue_received",
    name: "Tissue / Specimen Received",
  },
  {
    id: "lims-missing-info",
    key: "missing_information",
    name: "Missing / Incomplete Order Information",
  },
  {
    id: "lims-pending-signature",
    key: "pending_signature",
    name: "Pending Clinician Signature / Authorization",
  },
  {
    id: "lims-tissue-request",
    key: "tissue_request",
    name: "Tissue Request to Pathology",
  },
  {
    id: "lims-report-available",
    key: "report_available",
    name: "Test Report / Results Available",
  },
  {
    id: "lims-amended-report",
    key: "amended_report",
    name: "Amended / Corrected Report",
  },
  {
    id: "lims-insurance-issue",
    key: "insurance_issue",
    name: "Insurance / Billing Issue",
  },
  {
    id: "lims-order-cancelled",
    key: "order_cancelled",
    name: "Order Cancelled",
  },
] as const;

export const STORAGE_KEYS = {
  setupComplete: (userId: number) => `cstl_comm_setup_${userId}`,
  csUpdated: (userId: number) => `cstl_cs_prefs_updated_${userId}`,
  digest: (userId: number) => `cstl_daily_digest_${userId}`,
} as const;

export const COMMUNICATION_API = {
  overview: "/v1/communication-preferences",
  defaults: "/v1/communication-preferences/defaults",
  organization: (organizationId: number | string) =>
    `/v1/communication-preferences/organizations/${organizationId}`,
  restoreDefaults: (organizationId: number | string) =>
    `/v1/communication-preferences/organizations/${organizationId}/restore-defaults`,
  externalNotifications: (organizationId: number | string) =>
    `/v1/communication-preferences/organizations/${organizationId}/external-notifications`,
  deliveryHistory: "/v1/communication-preferences/delivery-history",
  deliveryHistoryItem: (id: number | string) =>
    `/v1/communication-preferences/delivery-history/${id}`,
  digest: "/v1/notifications/digest",
} as const;

/** US-only timezone options for delivery scheduling. */
export const US_TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Phoenix", label: "Arizona Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
] as const;

export const DEFAULT_US_TIMEZONE = US_TIMEZONE_OPTIONS[0].value;

export function toUsTimezone(timezone?: string | null): string {
  if (
    timezone &&
    US_TIMEZONE_OPTIONS.some((option) => option.value === timezone)
  ) {
    return timezone;
  }
  return DEFAULT_US_TIMEZONE;
}

/** Shared native `<select>` styles for Communication Preferences (chevron + right padding). */
export const COMMUNICATION_SELECT_CLASS =
  "w-full border border-dropDownBorder rounded-md pl-3 pr-10 py-2 text-royalBlue bg-white appearance-none bg-no-repeat bg-[length:16px_16px] bg-[right_0.75rem_center]";

export const COMMUNICATION_SELECT_CHEVRON_STYLE = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4 6l4 4 4-4' stroke='%23172B4D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
} as const;
